import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../../App';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

const CTA_DEFAULT = { to: '/schedule', label: 'FREE 20-Minute Business Jumpstart Session', bg: '#F97316' };
const CTA_BY_PATH = {
  '/free-health-jumpstart': { to: '/free-health-jumpstart', label: 'FREE 20-Minute Health Jumpstart Session', bg: '#16A34A' },
  '/free-life-jumpstart': { to: '/free-life-jumpstart', label: 'FREE 20-Minute Life Jumpstart Session', bg: '#1D4ED8' },
};

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Programs', path: '/programs' },
  { name: 'Events', path: '/events' },
  { name: 'Resources', path: '/resources' },
  { name: 'Brands', path: '/brands' },
  { name: 'Contact', path: '/contact' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const cta = CTA_BY_PATH[location.pathname] || CTA_DEFAULT;
  const { user } = useAuth();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="nav-glass sticky top-0 z-50" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between py-4" style={{ height: 76 }}>
          {/* Logo - 1.8x original size, polished render to avoid blur on retina/zoom */}
          <Link to="/" className="flex items-center gap-3 overflow-visible" data-testid="logo-link" style={{ height: 76 }}>
            <img
              src={LOGO_URL}
              alt="DCA Enterprises"
              className="w-auto"
              decoding="async"
              loading="eager"
              style={{
                height: 86,
                maxHeight: 86,
                objectFit: 'contain',
                objectPosition: 'center',
                imageRendering: '-webkit-optimize-contrast',
                WebkitFontSmoothing: 'antialiased',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-bold transition-colors ${
                  isActive(link.path)
                    ? 'text-[#1D4ED8]'
                    : 'text-[#0F172A] hover:text-[#1D4ED8]'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-5 ml-6 xl:ml-10 shrink-0">
            {user && user.role === 'client' && (
              <Link to="/portal" className="text-sm font-medium text-[#1D4ED8] hover:underline flex items-center gap-1" data-testid="portal-link">
                <User size={16} /> My Portal
              </Link>
            )}
            {user && (user.role === 'admin' || user.role === 'super_admin') && (
              <Link to="/admin" className="text-sm font-medium text-[#1D4ED8] hover:underline flex items-center gap-1" data-testid="admin-link">
                <User size={16} /> Admin
              </Link>
            )}
            {!user && (
              <Link to="/register" className="text-sm font-semibold text-[#1D4ED8] hover:underline whitespace-nowrap" data-testid="header-join-button">
                Join free
              </Link>
            )}
            {!user && (
              <Link
                to="/login"
                className="text-sm font-semibold text-[#0F172A] hover:text-[#1D4ED8] inline-flex items-center gap-1.5 px-3 py-2 border border-[#CBD5E1] rounded-lg hover:border-[#1D4ED8] transition"
                data-testid="header-login-button"
              >
                <User size={14} /> Log in
              </Link>
            )}
            <Link
              to={cta.to}
              className="btn-primary-orange !px-4 !py-2 text-center leading-tight rounded-lg"
              style={cta === CTA_DEFAULT ? undefined : { background: cta.bg, borderColor: cta.bg }}
              data-testid="header-cta-button"
            >
              <span className="block whitespace-nowrap">{cta.label.split(' Jumpstart')[0]}</span>
              <span className="block whitespace-nowrap">Jumpstart Session</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-[#0F172A]"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E2E8F0] py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-bold py-2 ${
                    isActive(link.path)
                      ? 'text-[#1D4ED8]'
                      : 'text-[#0F172A]'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to={cta.to}
                onClick={() => setMobileMenuOpen(false)}
                className="btn-primary-orange text-center mt-2"
                style={cta === CTA_DEFAULT ? undefined : { background: cta.bg, borderColor: cta.bg }}
                data-testid="mobile-cta-button"
              >
                {cta.label}
              </Link>
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-semibold text-center text-[#0F172A] py-2 border border-[#CBD5E1] rounded-lg"
                  data-testid="mobile-login-button"
                >
                  Log in
                </Link>
              )}
              {!user && (
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-center text-[#1D4ED8] py-2" data-testid="mobile-join-button">
                  Create a free account
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
